import { useEffect, useState } from "react";
import api, { extractErr } from "@/lib/api";

const KEY = "ms_ondemand_pin";

const LABEL_KEY = "ms_ondemand_pin_label";

export function getOndemandPin(slug) {
  try {
    return localStorage.getItem(`${KEY}:${slug}`) || "";
  } catch {
    return "";
  }
}

export function getOndemandPinLabel(slug) {
  try {
    return localStorage.getItem(`${LABEL_KEY}:${slug}`) || "";
  } catch {
    return "";
  }
}

export function setOndemandPin(slug, pin, label = "") {
  try {
    const digits = String(pin || "").replace(/\D/g, "").slice(0, 6);
    localStorage.setItem(`${KEY}:${slug}`, digits);
    const name = String(label || "").trim().slice(0, 48);
    if (name) localStorage.setItem(`${LABEL_KEY}:${slug}`, name);
    else localStorage.removeItem(`${LABEL_KEY}:${slug}`);
  } catch { /* ignore */ }
}

export function clearOndemandPin(slug) {
  try {
    localStorage.removeItem(`${KEY}:${slug}`);
    localStorage.removeItem(`${LABEL_KEY}:${slug}`);
  } catch { /* ignore */ }
}

export function OndemandPinGate({ slug, gate, onReady }) {
  const required = Boolean(gate?.required && gate?.has_zones);
  const [pin, setPin] = useState(() => getOndemandPin(slug));
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(() => required && !getOndemandPin(slug));

  useEffect(() => {
    if (!required) {
      onReady?.(true);
      return;
    }
    const saved = getOndemandPin(slug);
    if (saved) onReady?.(true);
  }, [required, slug, onReady]);

  useEffect(() => {
    const edit = () => setOpen(true);
    window.addEventListener("ondemand-pin-edit", edit);
    return () => window.removeEventListener("ondemand-pin-edit", edit);
  }, []);

  if (!required || !open) return null;

  const submit = async (e) => {
    e?.preventDefault();
    const digits = String(pin || "").replace(/\D/g, "").slice(0, 6);
    if (digits.length !== 6) {
      setMsg("Enter a 6-digit pincode");
      return;
    }
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post(`/store/${slug}/ondemand/check-pin`, { pincode: digits });
      if (data.servable) {
        setOndemandPin(slug, digits, data.label || "");
        setOpen(false);
        onReady?.(true);
        window.dispatchEvent(new CustomEvent("ondemand-pin", { detail: { pincode: digits, label: data.label || "" } }));
      } else {
        setMsg(data.message || gate.coming_soon_message || "Coming soon at your location");
      }
    } catch (err) {
      setMsg(extractErr(err) || "Could not check pincode");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="ondemand-pin-gate">
      <form onSubmit={submit} className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-6 shadow-xl">
        <div className="text-lg font-extrabold text-stone-900">Select delivery location</div>
        <p className="mt-1 text-sm text-stone-500">Enter your pincode to see products we can deliver.</p>
        <input
          inputMode="numeric"
          maxLength={6}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="Pincode"
          className="mt-4 h-12 w-full rounded-xl border border-stone-200 px-4 text-lg font-mono-nums"
          data-testid="ondemand-pin-input"
          autoFocus
        />
        {msg ? <p className="mt-2 text-sm text-red-600" data-testid="ondemand-pin-msg">{msg}</p> : null}
        <button type="submit" disabled={busy} className="mt-4 w-full h-11 rounded-full text-white font-bold" style={{ background: "var(--store-primary, #7C3AED)" }} data-testid="ondemand-pin-submit">
          {busy ? "Checking…" : "Continue"}
        </button>
      </form>
    </div>
  );
}
